/**
 * API Service - Handles all backend communication
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    // Don't clobber an explicitly-set header (e.g. the short-lived mfa token used
    // during login enrollment before a real session token exists).
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle token expiration
    if (error.response?.status === 401 && error.response?.data?.code === 'TOKEN_EXPIRED' && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
          const { token } = response.data;
          
          localStorage.setItem('token', token);
          originalRequest.headers.Authorization = `Bearer ${token}`;
          
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ============ Auth API ============
export const authAPI = {
  register: (username, password, email, inviteToken) =>
    api.post('/auth/register', { username, password, email, inviteToken }),

  login: (username, password) =>
    api.post('/auth/login', { username, password }),

  // 2FA
  verify2fa: (mfaToken, code) => api.post('/auth/login/verify', { mfaToken, code }),
  setup2fa: (authToken) =>
    api.post('/auth/2fa/setup', {}, authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {}),
  enable2fa: (code, authToken) =>
    api.post('/auth/2fa/enable', { code }, authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {}),
  disable2fa: (password) => api.post('/auth/2fa/disable', { password }),
  get2faStatus: () => api.get('/auth/2fa/status'),

  getMe: () => api.get('/auth/me'),

  updateProfile: (data) => api.put('/auth/profile', data),

  changePassword: (currentPassword, newPassword) =>
    api.put('/auth/password', { currentPassword, newPassword }),

  saveAlpacaKeys: (apiKey, secretKey, isPaperTrading = true) =>
    api.post('/auth/alpaca-keys', { apiKey, secretKey, isPaperTrading }),

  removeAlpacaKeys: () => api.delete('/auth/alpaca-keys'),

  refreshToken: (refreshToken) =>
    api.post('/auth/refresh', { refreshToken }),
};

// ============ Alpaca API ============
export const alpacaAPI = {
  getAccount: () => api.get('/alpaca/account'),

  getConnectionStatus: () => api.get('/alpaca/connection-status'),

  getPositions: () => api.get('/alpaca/positions'),

  getOrders: (status = 'all', limit = 50) =>
    api.get(`/alpaca/orders?status=${status}&limit=${limit}`),

  placeOrder: (order) => api.post('/alpaca/orders', order),

  cancelOrder: (orderId) => api.delete(`/alpaca/orders/${orderId}`),

  getQuotes: (symbols) => api.get(`/alpaca/quotes/${symbols}`),

  getBars: (symbol, timeframe = '1Day', limit = 100) =>
    api.get(`/alpaca/bars/${symbol}?timeframe=${timeframe}&limit=${limit}`),

  getSnapshot: (symbols) => api.get(`/alpaca/snapshot/${symbols}`),

  getBarsBatch: (symbols) => api.get(`/alpaca/bars-batch?symbols=${symbols}`),

  getMarketClock: () => api.get('/alpaca/clock'),

  getActivity: (activityType, limit = 50) =>
    api.get(`/alpaca/activity?activity_type=${activityType}&limit=${limit}`),

  verifyCredentials: (apiKey, secretKey, isPaper = true) =>
    api.post('/alpaca/verify', { apiKey, secretKey, isPaper }),

  getAssets: (search = '', limit = 100, offset = 0) =>
    api.get(`/alpaca/assets?search=${search}&limit=${limit}&offset=${offset}`),

  getPopularAssets: () => api.get('/alpaca/assets/popular'),
};

// ============ Analysis API ============
export const analysisAPI = {
  getTechnical: (symbol) => api.get(`/analysis/technical/${symbol}`),

  getFundamental: (symbol) => api.get(`/analysis/fundamental/${symbol}`),

  getSentiment: (symbol) => api.get(`/analysis/sentiment/${symbol}`),

  getSummary: (symbol) => api.get(`/analysis/summary/${symbol}`),

  getOpportunities: (strategy = 'swing') =>
    api.get(`/analysis/opportunities?strategy=${strategy}`),

  getWatchlist: () => api.get('/analysis/watchlist'),

  updateWatchlist: (symbols) => api.post('/analysis/watchlist', { symbols }),

  getNews: (symbol, strategy = 'swing') =>
    api.get(`/analysis/news/${symbol}?strategy=${strategy}`),

  getMarketNews: (strategy = 'swing', limit = 20) =>
    api.get(`/analysis/market-news?strategy=${strategy}&limit=${limit}`),

  // Confluence Engine (Phase 2)
  getConfluence: (symbol, options = {}) => {
    const params = new URLSearchParams();
    if (options.regime !== undefined) params.append('regime', options.regime);
    if (options.multitf !== undefined) params.append('multitf', options.multitf);
    return api.get(`/analysis/confluence/${symbol}?${params.toString()}`);
  },
  getQuickScore: (symbol) => api.get(`/analysis/quick-score/${symbol}`),
  batchScore: (symbols) => api.post('/analysis/batch-score', { symbols }),

  // Regime Adapter (Phase 3)
  getRegimeSummary: () => api.get('/analysis/regime-summary'),
  getRegimeAdaptedConfig: (symbol = 'SPY') => api.get(`/analysis/regime-adapted-config?symbol=${symbol}`),
  getRegimeProfiles: () => api.get('/analysis/regime-profiles'),

  // Renko & Martingale (Phase 4)
  getRenko: (symbol, brickSize) => api.get(`/analysis/renko/${symbol}${brickSize ? `?brickSize=${brickSize}` : ''}`),
  getSizingStatus: () => api.get('/analysis/sizing-status'),
  resetSizing: () => api.post('/analysis/sizing-reset'),

  // Prop Firm Guard (Phase 5)
  getPropFirmDashboard: () => api.get('/analysis/prop-firm/dashboard'),
  getPropFirmPresets: () => api.get('/analysis/prop-firm/presets'),
  setPropFirmBalance: (balance) => api.post('/analysis/prop-firm/set-balance', { balance }),
  resetPropFirm: () => api.post('/analysis/prop-firm/reset'),
  flattenAllPositions: () => api.post('/analysis/prop-firm/flatten'),

  // Multi-Timeframe & Regime Detection (Phase 1)
  getRegime: (symbol) => api.get(`/analysis/regime/${symbol}`),
  getMultiTF: (symbol) => api.get(`/analysis/multi-tf/${symbol}`),
  getTradingWindow: () => api.get('/analysis/trading-window'),
  getIntradayBars: (symbol, timeframe = '5Min', limit = 100) =>
    api.get(`/analysis/intraday-bars/${symbol}?timeframe=${timeframe}&limit=${limit}`),
  getMultiTFBars: (symbol, timeframes = '1Day,5Min') =>
    api.get(`/analysis/multi-tf-bars/${symbol}?timeframes=${timeframes}`),
  getIntradayCacheStats: () => api.get('/analysis/intraday-cache-stats'),
};

// ============ Bot API ============
export const botAPI = {
  getStatus: () => api.get('/bot/status'),

  getConfig: () => api.get('/bot/config'),

  updateConfig: (config) => api.put('/bot/config', config),

  start: () => api.post('/bot/start'),

  stop: () => api.post('/bot/stop'),

  getTrailingStops: () => api.get('/bot/trailing-stops'),

  getJournal: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/bot/journal${query ? '?' + query : ''}`);
  },

  updateJournalNote: (id, note) => api.put(`/bot/journal/${id}/note`, { note }),

  // Backtest
  runBacktest: (config) => api.post('/backtest/run', config),
  runPortfolioBacktest: (config) => api.post('/backtest/portfolio', config),
  compareStrategies: (config) => api.post('/backtest/compare', config),
  getDataStats: () => api.get('/backtest/data-stats'),
  fetchHistoricalData: (symbol, yearsBack) => api.post('/backtest/fetch-data', { symbol, yearsBack }),
  fetchHistoricalBatch: (symbols, yearsBack) => api.post('/backtest/fetch-data-batch', { symbols, yearsBack }),
  runOptimizer: (config) => api.post('/backtest/optimize', config),
  getAtrConfig: () => api.get('/bot/atr-config'),
  updateAtrConfig: (atrStopMultiplier) => api.put('/bot/atr-config', { atrStopMultiplier }),
  getPerformance: () => api.get('/bot/performance'),

  // Notifications
  getNotificationSettings: () => api.get('/notifications/settings'),
  updateNotificationSettings: (settings) => api.put('/notifications/settings', settings),
  testNotification: (data) => api.post('/notifications/test', data),

  // Price Alerts
  getAlerts: (all) => api.get(`/alerts${all ? '?all=true' : ''}`),
  createAlert: (alert) => api.post('/alerts', alert),
  deleteAlert: (id) => api.delete(`/alerts/${id}`),
  toggleAlert: (id) => api.put(`/alerts/${id}/toggle`),
  clearTriggeredAlerts: () => api.delete('/alerts/triggered/clear'),

  // DCA
  getDcaConfig: () => api.get('/dca/config'),
  updateDcaConfig: (config) => api.put('/dca/config', config),
  getDcaHistory: (symbol) => api.get(`/dca/history${symbol ? '?symbol=' + symbol : ''}`),
  getDcaStats: () => api.get('/dca/stats'),

  // Strategy Templates
  getTemplates: (category) => api.get(`/templates${category ? '?category=' + category : ''}`),
  getTemplate: (id) => api.get(`/templates/${id}`),
  applyTemplate: (id) => api.post(`/templates/${id}/apply`),
  getActiveTemplate: () => api.get('/templates/active/current'),

  // Stock Scanner
  runScan: (options) => api.post('/scanner/scan', options),
  getScannerPresets: () => api.get('/scanner/presets'),
  getScannerMovers: () => api.get('/scanner/movers'),

  getHistory: (limit = 50, offset = 0) =>
    api.get(`/bot/history?limit=${limit}&offset=${offset}`),

  clearHistory: () => api.delete('/bot/history'),

  // Guru / SEC EDGAR
  getGurus: () => api.get('/bot/gurus'),
  getGuruHoldings: (guruId) => api.get(`/bot/gurus/${guruId}/holdings`),
  getAllGuruHoldings: () => api.get('/bot/gurus/all-holdings'),
  refreshGuruCache: (guruId) => api.post('/bot/gurus/refresh', { guruId }),
};

// ============ Config API ============
export const configAPI = {
  getDayTradingEnabled: () => api.get('/config/day-trading-enabled'),
  getWsbEnabled: () => api.get('/config/wsb-enabled'),
};

// ============ WSB API ============
export const wsbAPI = {
  getTrending: (limit = 10) => api.get(`/wsb/trending?limit=${limit}`),

  getPosts: (sort = 'hot', limit = 25) => 
    api.get(`/wsb/posts?sort=${sort}&limit=${limit}`),

  getTickerAnalysis: (symbol) => api.get(`/wsb/ticker/${symbol}`),

  getSignals: (minMentions = 3, minConfidence = 50, maxRisk = 70) =>
    api.get(`/wsb/signals?minMentions=${minMentions}&minConfidence=${minConfidence}&maxRisk=${maxRisk}`),

  getSentimentSummary: () => api.get('/wsb/sentiment-summary'),
};

// ============ Options API ============
export const optionsAPI = {
  // Get IV analysis for a symbol
  getIV: (symbol) => api.get(`/options/iv/${symbol}`),

  // Get market regime analysis
  getRegime: (symbol) => api.get(`/options/regime/${symbol}`),

  // Get complete options analysis and trade recommendation
  analyze: (symbol, bias = null) => 
    api.get(`/options/analyze/${symbol}${bias ? `?bias=${bias}` : ''}`),

  // Get available strategies
  getStrategies: () => api.get('/options/strategies'),

  // Get strategy recommendations based on market view
  selectStrategy: (symbol, marketView) => 
    api.post('/options/strategy-select', { symbol, marketView }),

  // Calculate position size
  calculatePositionSize: (optionPrice, riskPercent) =>
    api.post('/options/position-size', { optionPrice, riskPercent }),

  // Check liquidity requirements
  checkLiquidity: (bid, ask, openInterest, volume) =>
    api.post('/options/check-liquidity', { bid, ask, openInterest, volume }),

  // Scan multiple symbols for opportunities
  scan: (symbols = null) => 
    api.get(`/options/scan${symbols ? `?symbols=${symbols}` : ''}`),

  // Get economic calendar
  getCalendar: () => api.get('/options/calendar'),

  // Check exit conditions
  checkExit: (entryPremium, currentPremium, strategy, daysHeld) =>
    api.post('/options/exit-check', { entryPremium, currentPremium, strategy, daysHeld }),
};

// ============ Crypto API ============
export const cryptoAPI = {
  // Get available universes and symbols
  getUniverse: () => api.get('/crypto/universe'),

  // Get risk profile configurations
  getProfiles: () => api.get('/crypto/profiles'),

  // Get specific profile details
  getProfile: (profileKey) => api.get(`/crypto/profile/${profileKey}`),

  // Analyze a specific symbol
  analyze: (symbol, profile = 'medium') => 
    api.get(`/crypto/analyze/${symbol}?profile=${profile}`),

  // Scan universe for opportunities
  scan: (universe = 'curated', profile = 'medium') => 
    api.get(`/crypto/scan?universe=${universe}&profile=${profile}`),

  // Get current quote
  getQuote: (symbol) => api.get(`/crypto/quote/${symbol}`),

  // Get market regime
  getRegime: (symbol) => api.get(`/crypto/regime/${symbol}`),

  // Calculate position size
  calculatePosition: (symbol, riskProfile, stopDistance) =>
    api.post('/crypto/calculate-position', { symbol, riskProfile, stopDistance }),

  // Get crypto portfolio
  getPortfolio: () => api.get('/crypto/portfolio'),

  // Get fee tier info
  getFeeTier: () => api.get('/crypto/fee-tier'),

  // Validate position
  validatePosition: (params) => api.post('/crypto/validate-position', params),

  // Check correlation
  checkCorrelation: (symbol) => api.post('/crypto/check-correlation', { symbol }),

  // Get risk config
  getRiskConfig: () => api.get('/crypto/risk-config'),
};

// ============ Crypto News API ============
export const cryptoNewsAPI = {
  // Get news sources
  getSources: () => api.get('/crypto-news/sources'),

  // Get trackable coins
  getCoins: () => api.get('/crypto-news/coins'),

  // Get news feed
  getFeed: (maxAge = 24, limit = 50) => 
    api.get(`/crypto-news/feed?maxAge=${maxAge}&limit=${limit}`),

  // Get market sentiment
  getMarketSentiment: (maxAge = 24) => 
    api.get(`/crypto-news/market-sentiment?maxAge=${maxAge}`),

  // Get coin-specific sentiment
  getCoinSentiment: (symbol, maxAge = 24) => 
    api.get(`/crypto-news/coin/${symbol}?maxAge=${maxAge}`),

  // Get trading signals
  getSignals: (minImpact = 60, maxAge = 24) => 
    api.get(`/crypto-news/signals?minImpact=${minImpact}&maxAge=${maxAge}`),

  // Search news
  search: (query, coin, maxAge = 24) => 
    api.get(`/crypto-news/search?${query ? `q=${query}` : ''}${coin ? `&coin=${coin}` : ''}&maxAge=${maxAge}`),

  // Get trending
  getTrending: (maxAge = 6) => 
    api.get(`/crypto-news/trending?maxAge=${maxAge}`),

  // Get high-impact alerts
  getAlerts: (minImpact = 70, maxAge = 6) => 
    api.get(`/crypto-news/alerts?minImpact=${minImpact}&maxAge=${maxAge}`),

  // Get tracked influencers
  getInfluencers: () => api.get('/crypto-news/influencers'),

  // Get influencer alerts (Elon Musk, etc.)
  getInfluencerAlerts: (maxAge = 6) => 
    api.get(`/crypto-news/influencer-alerts?maxAge=${maxAge}`),

  // Force refresh
  refresh: () => api.post('/crypto-news/refresh'),
};

// ============ Unified News API ============
export const newsAPI = {
  // Get aggregated news feed from all sources
  getFeed: (maxAge = 24, limit = 100) =>
    api.get(`/news/feed?maxAge=${maxAge}&limit=${limit}`),

  // Get market summary with sentiment
  getMarketSummary: () => api.get('/news/market-summary'),

  // Get high-impact alerts
  getAlerts: (minImpact = 70, maxAge = 6) =>
    api.get(`/news/alerts?minImpact=${minImpact}&maxAge=${maxAge}`),

  // Get trending assets
  getTrending: (maxAge = 6) =>
    api.get(`/news/trending?maxAge=${maxAge}`),

  // Get news for specific asset
  getAssetNews: (symbol, maxAge = 24) =>
    api.get(`/news/asset/${symbol}?maxAge=${maxAge}`),

  // Get news categories
  getCategories: () => api.get('/news/categories'),

  // Get overall market sentiment
  getSentiment: (maxAge = 24) =>
    api.get(`/news/sentiment?maxAge=${maxAge}`),
};

// ============ Positions API (Phase 1) ============
export const positionsAPI = {
  getATR: (symbol, period = 14) =>
    api.get(`/positions/atr/${symbol}?period=${period}`),

  analyze: (params) =>
    api.post('/positions/analyze', params),

  calculateSize: (params) =>
    api.post('/positions/size', params),

  generatePlan: (params) =>
    api.post('/positions/plan', params),

  getActive: () =>
    api.get('/positions/active'),
};

// ============ Strategies API (Phase 2) ============
export const strategiesAPI = {
  getList: () =>
    api.get('/strategies/list'),

  analyze: (symbol) =>
    api.get(`/strategies/analyze/${symbol}`),

  compare: (symbols, strategy) =>
    api.post('/strategies/compare', { symbols, strategy }),
};

// ============ Logs API ============
export const logsAPI = {
  // Get log stats
  getStats: () => api.get('/logs/stats'),

  // Get recent logs
  getRecent: (type = 'combined', lines = 100) =>
    api.get(`/logs/recent?type=${type}&lines=${lines}`),

  // Get recent errors
  getErrors: (count = 50) =>
    api.get(`/logs/errors?count=${count}`),

  // Get action logs
  getActions: (lines = 100) =>
    api.get(`/logs/actions?lines=${lines}`),

  // Get API logs
  getApiLogs: (lines = 100) =>
    api.get(`/logs/api?lines=${lines}`),

  // Get trade logs
  getTrades: (lines = 100) =>
    api.get(`/logs/trades?lines=${lines}`),

  // Set log level
  setLevel: (level) =>
    api.post('/logs/level', { level }),

  // Clear old logs
  clearOld: (daysOld = 7) =>
    api.post('/logs/clear', { daysOld }),
};

export default api;
